import React, { useEffect, useMemo, useRef, useState } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import useMediaQuery from "@mui/material/useMediaQuery";

import { widgetById, isEditable, snapWidth, customWidthFor, GRID_COLS, ROW_HEIGHT, GRID_MARGIN } from "./widgetRegistry";
import "./customize.css";

const Grid = WidthProvider(GridLayout);

/** The card's own vertical padding + border — what the interior doesn't cover. */
const chromeOf = (card) => {
  if (!card || typeof window === "undefined") return 0;
  const cs = window.getComputedStyle(card);
  return ["paddingTop", "paddingBottom", "borderTopWidth", "borderBottomWidth"].reduce(
    (sum, k) => sum + (parseFloat(cs[k]) || 0),
    0,
  );
};

/**
 * Heights for the cards that hug their content (registry `hug: true`).
 *
 * Hugging resolves per ROW, never per card: sizing each card to its own content
 * leaves a row with ragged bottoms — a tall card beside a short one — which
 * reads as broken. So a card takes the height of the TALLEST thing sharing its
 * row: the tallest measured content, or a fixed-height neighbour like the
 * Mountain. The row closes up when everything in it is short, and a card only
 * stretches because a real neighbour needs the space.
 *
 * "Sharing its row" is decided by OVERLAPPING vertical span, not by an equal
 * `y`. Boards are hand-arranged (compactType is null), so two cards that sit
 * side by side often start a row or two apart — keying on `y` equality treated
 * those as separate rows and the ragged bottoms came straight back. Overlap is
 * measured on the SAVED spans, so a card that shrinks doesn't drop out of the
 * row it belongs to. Cards that merely touch (one ends where the next begins)
 * do not overlap, and stay independent.
 *
 * @param layout   the board's blocks
 * @param hugRows  measured row counts, by block id
 * @returns {Object} pinned height by block id — only for hugging, measured cards
 */
export const resolveHugHeights = (layout, hugRows) => {
  const wanted = (b) => {
    const def = widgetById(b.i);
    const m = def?.hug ? hugRows[b.i] : null;
    if (!m) return b.h;
    return Math.min(Math.max(m, def.minH), def.maxH);
  };
  const overlaps = (a, b) => a.y < b.y + b.h && b.y < a.y + a.h;

  const isFixed = (c) => {
    const d = widgetById(c.i);
    return !d?.hug || !hugRows[c.i];
  };

  const out = {};
  layout.forEach((b) => {
    const def = widgetById(b.i);
    if (!def?.hug || !hugRows[b.i]) return;
    const row = layout.filter((c) => overlaps(b, c));
    /* A FIXED neighbour governs the row. The Mountain and the milestone chart
       are compositions sized by hand — they cannot grow to meet a hugging card,
       so if the hugging card followed its own content it would simply stick out
       past them and the row would be ragged again, which is the thing we set out
       to kill. When the row holds anything fixed, the hugging card takes that
       height and scrolls its own list if the content needs more. Only a row made
       entirely of hugging cards is free to follow its content. */
    const fixed = row.filter(isFixed);
    const rowMax = (fixed.length ? fixed : row).reduce((max, c) => Math.max(max, wanted(c)), 0);
    /* Matching the row has a limit. A single figure beside the 568px Greeting
       would be stretched more than three times its natural height, and no graph
       fills that honestly — you get a tall column of nothing. Past twice its own
       height the card stops growing: the pairing is the mistake at that point,
       not the height. Up to 2x, the card grows and its graph takes the slack. */
    const natural = wanted(b);
    const capped = Math.max(natural, Math.min(rowMax, natural * 2));
    out[b.i] = Math.min(Math.max(capped, def.minH), def.maxH);
  });
  return out;
};

/**
 * The dashboard board itself.
 *
 * View mode renders the exact same grid, only static — players see zero
 * customize chrome. Edit mode turns on dragging (whole block is the drag zone),
 * corner resize within each widget's registry limits, and a floating glass pill
 * naming the block with its remove control.
 *
 * Rearranging is a true SWAP (same as the metrics dashboards): drop a block
 * onto another and the two exchange slots — position AND size — and nothing
 * else moves. This is why the grid runs compactType={null} + preventCollision
 * (no auto-reflow); the swap is applied manually from the pointer position on
 * drop.
 *
 * Below the lg breakpoint the board renders as a plain stacked column in saved
 * order — matching how the dashboard has always stacked on mobile.
 */
const DashboardCanvas = ({ layout, editing, onLayoutChange, onRemove, renderWidget, onStartTemplate, onAddComponent, draggingId, onDropWidget, onEditWidget, ghostId, focusId, popId }) => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const gridWrapRef = useRef(null);
  const dragInfo = useRef(null); // { id, snapshot } captured on drag start
  const resizeInfo = useRef(null); // { id, snapshot, startPt } captured on resize start
  const skipNext = useRef(false); // ignore the grid's own layout echo after a swap
  const [swapTarget, setSwapTarget] = useState(null);

  /* ── cards that hug their content ────────────────────────────────────────
     Most cards are compositions you size by hand. But a few are only as tall
     as what the admin put in them — Anticipated earnings with its chips off and
     three rules is a shorter card than one with chips on and six, and a custom
     widget holding one metric is shorter than one holding two plus a bonus.
     Those defs carry `hug: true`: the row they sit in follows their content
     instead of a saved number, so a row closes up instead of leaving dead space.

     What gets measured is the card's `data-hug-content` interior, NOT the card:
     the card is stretched to fill whatever height the measurement decides, so
     observing the card would only report our own answer back (and it could then
     never shrink). Heights end up pinned (minH === maxH), which leaves
     width-only resizing — dragging a height that content overrides is a lie. */
  const [hugRows, setHugRows] = useState({});
  /* Heights are FROZEN while the board is being edited. A hugging card measures
     its own content, and entering Customize changes that content just enough
     (drag chrome, the card's own controls appearing) to re-pin its height — so
     Anticipated earnings grew a row and shoved everything below it down the
     page, every single time the Customize button was pressed. Nothing about
     arranging a board should change how tall its cards are; the measurement
     resumes the moment editing ends. */
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const hugObs = useRef(null);
  const hugBodies = useRef(new Map()); // block id -> .gmbw-body
  const hugWatched = useRef(new Set()); // interiors currently observed
  const hugCbs = useRef(new Map());

  const rowsForPx = (px) => {
    const my = GRID_MARGIN[1];
    return Math.max(1, Math.ceil((px + my) / (ROW_HEIGHT + my)));
  };

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const seen = {};
      entries.forEach((en) => {
        const host = en.target.closest("[data-hug-id]");
        const id = host && host.getAttribute("data-hug-id");
        if (!id) return;
        const px = en.target.getBoundingClientRect().height + chromeOf(en.target.parentElement);
        if (px > 0) seen[id] = rowsForPx(px);
      });
      if (!Object.keys(seen).length) return;
      if (editingRef.current) return; // frozen — see editingRef above
      setHugRows((prev) => {
        const changed = Object.keys(seen).some((id) => prev[id] !== seen[id]);
        return changed ? { ...prev, ...seen } : prev;
      });
    });
    hugObs.current = ro;
    const watched = hugWatched.current;
    return () => {
      ro.disconnect();
      hugObs.current = null;
      watched.clear();
    };
  }, []);

  /* The interior can arrive after the body does — a card renders an empty state
     first and its real content once data lands — so re-check every render and
     pick up any interior not being watched yet. Cheap: a handful of nodes. */
  useEffect(() => {
    const ro = hugObs.current;
    if (!ro) return;
    hugBodies.current.forEach((body) => {
      const inner = body.querySelector("[data-hug-content]");
      if (inner && !hugWatched.current.has(inner)) {
        hugWatched.current.add(inner);
        ro.observe(inner);
      }
    });
  });

  // One stable callback ref per block, so re-renders don't churn the observer.
  const hugRefFor = (id) => {
    if (!hugCbs.current.has(id)) {
      hugCbs.current.set(id, (el) => {
        if (el) hugBodies.current.set(id, el);
        else hugBodies.current.delete(id);
      });
    }
    return hugCbs.current.get(id);
  };

  // A just-added widget lands at the bottom of the board, off-screen — so scroll
  // it into view. Without this the user "adds" a widget and sees nothing move.
  useEffect(() => {
    if (!popId) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-block-id="${popId}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    return () => clearTimeout(t);
  }, [popId]);

  // A card dragged out of the library previews at that component's own default
  // size, so the placeholder you see is the space it will really take.
  const incoming = draggingId ? widgetById(draggingId) : null;
  const droppingItem = incoming
    ? { i: `__drop_${incoming.id}`, w: incoming.defaultW, h: incoming.defaultH }
    : undefined;

  // react-grid-layout wants constraints ON the layout items.
  const gridLayout = useMemo(() => {
    const heights = resolveHugHeights(layout, hugRows);
    return layout.map((b) => {
      const def = widgetById(b.i);
      const item = {
        ...b,
        minW: def?.minW,
        maxW: def?.maxW,
        minH: def?.minH,
        maxH: def?.maxH,
        isResizable: editing && def?.resizable !== false,
      };
      const h = heights[b.i];
      if (h) {
        item.h = h;
        item.minH = h; // height is content-driven, so only width stays draggable
        item.maxH = h;
      }
      return item;
    });
  }, [layout, editing, hugRows]);

  // Grid coordinates (fractional col/row) of a pointer event.
  const gridPoint = (e) => {
    if (!e) return null;
    const gridEl = gridWrapRef.current?.querySelector(".react-grid-layout");
    if (!gridEl) return null;
    const rect = gridEl.getBoundingClientRect();
    const point = e.changedTouches?.[0] || e.touches?.[0] || e;
    if (point.clientX == null) return null;
    const [mx, my] = GRID_MARGIN;
    const colW = (rect.width - mx * (GRID_COLS - 1)) / GRID_COLS;
    return {
      col: (point.clientX - rect.left) / (colW + mx),
      row: (point.clientY - rect.top) / (ROW_HEIGHT + my),
    };
  };

  // The block (other than the one being dragged) under the pointer, if any.
  const targetAtPointer = (e) => {
    const info = dragInfo.current;
    const pt = gridPoint(e);
    if (!info || !pt) return null;
    return (
      info.snapshot.find(
        (b) =>
          b.i !== info.id &&
          pt.col >= b.x && pt.col < b.x + b.w &&
          pt.row >= b.y && pt.row < b.y + b.h,
      ) || null
    );
  };

  const clampWH = (b) => {
    const def = widgetById(b.i);
    if (!def) return b;
    return {
      ...b,
      w: Math.min(Math.max(b.w, def.minW), def.maxW),
      h: Math.min(Math.max(b.h, def.minH), def.maxH),
    };
  };

  const onDragStart = (l, oldItem) => {
    skipNext.current = false;
    dragInfo.current = { id: oldItem.i, snapshot: layout.map((b) => ({ ...b })) };
  };

  const onDrag = (l, oldItem, newItem, placeholder, e) => {
    const t = targetAtPointer(e);
    const id = t ? t.i : null;
    if (id !== swapTarget) setSwapTarget(id);
  };

  /* Two cards trade PLACES, never sizes. Taking the other card's w/h as well
     was the whole reason a drop looked like an explosion: drag a small card
     onto a big one and the small one inflated, the big one shrank, and both
     jumped — two cards changing shape and position at once. A card keeps the
     size it has; only x/y are exchanged. */
  const hits = (a, b) =>
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

  /* Where a displaced card goes: the NEAREST free space, searched outward from
     where it already is. Pushing it straight down instead — the obvious move —
     compounds: it lands on the next card, which pushes that one down onto the
     one after it, and a card that only needed to shift aside ends up a thousand
     pixels down the page. Measured: Team Performance travelled 1,160px that way.
     Nearest-free keeps it beside where it was, usually in the hole the swap just
     opened up. Vertical distance is weighted slightly heavier than horizontal so
     a card prefers sliding sideways over dropping down a row. */
  const nearestFree = (b, occupied) => {
    const free = (x, y) => !occupied.some((o) => hits(o, { ...b, x, y }));
    const limit = occupied.reduce((m, o) => Math.max(m, o.y + o.h), 0) + b.h;
    const scan = (rows) => {
      let best = null;
      let bestD = Infinity;
      rows.forEach((y) => {
        for (let x = 0; x <= GRID_COLS - b.w; x += 1) {
          if (!free(x, y)) continue;
          const d = Math.abs(x - b.x) + Math.abs(y - b.y) * 1.25;
          if (d < bestD) { bestD = d; best = { x, y }; }
        }
      });
      return best;
    };
    /* Land on a row that already exists before inventing a new one. Purely
       nearest-free put a card two rows below its neighbour's top edge — free,
       and closer by the numbers, but 80px out of line with the card beside it.
       Rows first, then anywhere. */
    const tops = [...new Set([0, ...occupied.map((o) => o.y)])].sort((m, n) => m - n);

    /* A displaced card moves the LEAST it can, and never travels. Searching the
       whole board for "nearest free" is what sent a card a thousand pixels down
       the page — free by the numbers, absurd on screen. So look only within a
       short radius of where it already sits (its own height plus one row, which
       is exactly "nudge it aside"), and settle for a small overlap-free shift
       rather than a long journey. Only if nothing at all fits nearby does the
       full board come into play. */
    const near = tops.filter((y) => Math.abs(y - b.y) <= b.h + 1);
    const nearRows = Array.from(
      { length: b.h + 2 },
      (unused, i) => Math.max(0, b.y + i),
    );
    const all = Array.from({ length: limit + 1 }, (unused, i) => i);
    return scan(near) || scan(nearRows) || scan(tops) || scan(all) || { x: b.x, y: b.y };
  };

  const nudgeAway = (blocks, anchorIds) => {
    const out = blocks.map((b) => ({ ...b }));
    const anchors = new Set(anchorIds);
    const settled = out.filter((b) => anchors.has(b.i));
    // Closest first, so the card with the least to move claims its spot first.
    const rest = out
      .filter((b) => !anchors.has(b.i))
      .sort((a, c) => a.y - c.y || a.x - c.x);
    rest.forEach((b) => {
      if (settled.some((o) => hits(o, b))) {
        const spot = nearestFree(b, settled);
        b.x = spot.x;
        b.y = spot.y;
      }
      settled.push(b);
    });
    return out;
  };

  const onDragStop = (l, oldItem, newItem, placeholder, e) => {
    const info = dragInfo.current;
    const target = targetAtPointer(e);
    dragInfo.current = null;
    setSwapTarget(null);
    if (!info) return;
    const src = info.snapshot.find((b) => b.i === info.id);
    if (!src) return;

    if (target) {
      skipNext.current = true;
      /* Keeping its own width means a half-width card dropped near the right
         edge would hang off the end of the 24 columns — the one case where a
         card cannot land exactly where it was released. Slide it just far
         enough to sit inside the grid. */
      const fit = (b) => ({ ...b, x: Math.max(0, Math.min(b.x, GRID_COLS - b.w)) });
      const swapped = info.snapshot.map((b) => {
        if (b.i === src.i) return clampWH(fit({ ...b, x: target.x, y: target.y }));
        if (b.i === target.i) return clampWH(fit({ ...b, x: src.x, y: src.y }));
        return b;
      });
      onLayoutChange(nudgeAway(swapped, [src.i, target.i]));
      return;
    }

    // Dropped over empty space. With preventCollision the grid freezes its
    // placeholder at the last non-colliding spot, so a long drag (e.g.
    // straight down past another block) landed short of where the block was
    // released. Re-derive the intended cell from how far the pointer actually
    // travelled and apply it whenever the destination is free.
    const pt = gridPoint(e);
    if (!pt || !info.startPt) return;
    const x = Math.max(0, Math.min(GRID_COLS - src.w, Math.round(src.x + pt.col - info.startPt.col)));
    const y = Math.max(0, Math.round(src.y + pt.row - info.startPt.row));
    const collides = info.snapshot.some(
      (b) => b.i !== src.i && x < b.x + b.w && x + src.w > b.x && y < b.y + b.h && y + src.h > b.y,
    );
    if (collides || (x === src.x && y === src.y)) return; // keep the grid's own result
    skipNext.current = true;
    onLayoutChange(info.snapshot.map((b) => (b.i === src.i ? { ...b, x, y } : b)));
  };

  const onResizeStart = (l, oldItem, newItem, placeholder, e) => {
    resizeInfo.current = {
      id: oldItem.i,
      snapshot: layout.map((b) => ({ ...b })),
      startPt: gridPoint(e),
    };
  };

  // preventCollision clamps a resize the moment it touches a neighbour, so
  // dragging the corner down past another block used to do nothing on release.
  // Instead: apply the size the pointer actually asked for (within the
  // widget's own limits) and push every overlapped block downward, cascading.
  const onResizeStop = (l, oldItem, newItem, placeholder, e) => {
    const info = resizeInfo.current;
    resizeInfo.current = null;
    const pt = gridPoint(e);
    if (!info || !info.startPt || !pt) return;
    const src = info.snapshot.find((b) => b.i === info.id);
    const def = widgetById(info.id);
    if (!src || !def) return;

    const maxW = Math.min(def.maxW ?? GRID_COLS, GRID_COLS - src.x);
    let w = Math.max(def.minW ?? 1, Math.min(maxW, Math.round(src.w + pt.col - info.startPt.col)));
    const h = Math.max(def.minH ?? 1, Math.min(def.maxH ?? Infinity, Math.round(src.h + pt.row - info.startPt.row)));

    /* Custom components resize in whole steps — quarter, half, full — never to
       whatever column the pointer happened to be over. Free-dragging a width is
       how a board ends up with a 7-column card beside a 5-column one and someone
       squinting at the gap between them; there is nothing to judge if only three
       widths exist. Height is untouched: it is decided by the content and the row. */
    if (def.widthSteps) {
      const floor = def.custom ? customWidthFor(src?.s) : def.minW;
      w = Math.min(Math.max(snapWidth(w), floor), GRID_COLS - src.x);
    }

    if (w === newItem.w && h === newItem.h) return; // grid already applied it

    const next = info.snapshot.map((b) => (b.i === src.i ? { ...b, w, h } : { ...b }));
    const overlap = (a, b) =>
      a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
    const queue = [next.find((b) => b.i === src.i)];
    while (queue.length) {
      const cur = queue.shift();
      next.forEach((b) => {
        if (b.i !== cur.i && b.i !== src.i && overlap(cur, b)) {
          b.y = cur.y + cur.h; // pushed below whatever grew/moved into it
          queue.push(b);
        }
      });
    }
    skipNext.current = true;
    onLayoutChange(next);
  };

  const handleLayoutChange = (next) => {
    if (skipNext.current) {
      skipNext.current = false;
      return; // a swap was just applied; ignore the grid's own echo
    }
    onLayoutChange(next);
  };

  // Stacked order for mobile: row by row, left to right.
  const stackOrder = useMemo(
    () => [...layout].sort((a, b) => a.y - b.y || a.x - b.x),
    [layout],
  );

  if (isSmallScreen) {
    return (
      <div className="gmbw-stack">
        {stackOrder.map((b) => (
          <div key={b.i}>{renderWidget(b.i)}</div>
        ))}
      </div>
    );
  }

  if (editing && layout.length === 0) {
    return (
      <div
        className="gmbw-canvas"
        onDragOver={(e) => { if (incoming) e.preventDefault(); }}
        onDrop={(e) => { if (!incoming) return; e.preventDefault(); onDropWidget?.(incoming.id, 0, 0); }}
      >
        <div className={`gmbw-empty-canvas${incoming ? " droppable" : ""}`}>
          <span className="gmbw-empty-title">Your board is empty</span>
          <span className="gmbw-empty-sub">Start from a template, or add components one at a time.</span>
          <span className="gmbw-empty-actions">
            <button type="button" className="gmbw-btn primary" onClick={onStartTemplate}>Start from a template</button>
            <button type="button" className="gmbw-btn" onClick={onAddComponent}>Add component</button>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="gmbw-canvas" ref={gridWrapRef}>
      <Grid
        layout={gridLayout}
        cols={GRID_COLS}
        rowHeight={ROW_HEIGHT}
        margin={GRID_MARGIN}
        containerPadding={[0, 0]}
        isDraggable={editing}
        isResizable={editing}
        draggableHandle=".gmbw-dragzone"
        compactType={null}
        preventCollision
        onLayoutChange={handleLayoutChange}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragStop={onDragStop}
        onResizeStart={onResizeStart}
        onResizeStop={onResizeStop}
        isDroppable={editing && !!incoming}
        droppingItem={droppingItem}
        onDrop={(l, item) => {
          if (!incoming || !item) return;
          onDropWidget?.(incoming.id, item.x, item.y);
        }}
        useCSSTransforms
      >
        {layout.map((b) => {
          const def = widgetById(b.i);
          const ghost = b.i === ghostId;
          /* While one card is being created or edited, it is the focus and the
             rest of the board steps back — nobody should have to hunt for
             which card is the one changing. */
          const dimmed = focusId && b.i !== focusId;
          return (
            <div
              key={b.i}
              data-block-id={b.i}
              className={
                [
                  editing && swapTarget === b.i ? "gmbw-swaptarget" : "",
                  ghost ? "gmbw-ghost" : "",
                  focusId && b.i === focusId ? "gmbw-focused" : "",
                  dimmed ? "gmbw-dimmed" : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
            >
              <div className={`gmbw-shell${editing ? " editing" : ""}${def?.hug ? " hug" : ""}${b.i === popId ? " gmbw-pop" : ""}`}>
                {ghost && <span className="gmbw-ghosttag">Not added yet</span>}
                <div
                  className="gmbw-body"
                  ref={def?.hug ? hugRefFor(b.i) : undefined}
                  data-hug-id={def?.hug ? b.i : undefined}
                  style={def?.scroll ? { overflowY: "auto" } : undefined}
                >
                  {renderWidget(b.i)}
                </div>
                {editing && !ghost && (
                  <>
                    {/* The whole card body is the drag handle. A small identity
                        chip sits top-left (so cards without their own visible
                        title — Podium, Greeting — are still nameable), and the
                        actions live top-right where card actions belong, clear
                        of the card's own header. Both stop propagation so a
                        click never starts a drag. */}
                    <div className="gmbw-dragzone" aria-hidden="true">
                      <span className="gmbw-drag-hint">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>
                        Drag to move
                      </span>
                    </div>
                    {/* The identity chip names the block — but only for cards
                        that don't already print their own title. On earnings or
                        the Mountain it repeated the heading a few pixels below
                        it, twice in the same eyeline. `renameable` is the tell:
                        a card is renameable precisely because it shows a title. */}
                    {!def?.renameable && <span className="gmbw-tagchip">{def?.title || b.i}</span>}
                    <span className="gmbw-cardacts">
                      {isEditable(b.i) && (
                        <button
                          type="button"
                          className="gmbw-cardact"
                          aria-label={`Edit ${def?.title || b.i}`}
                          title="Edit this component"
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onClick={() => onEditWidget?.(b.i)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                        </button>
                      )}
                      <button
                        type="button"
                        className="gmbw-cardact danger"
                        aria-label={`Remove ${def?.title || b.i}`}
                        title="Remove from board"
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onClick={() => onRemove(b.i)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                      </button>
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </Grid>
    </div>
  );
};

export default DashboardCanvas;
